import { useState, useEffect, useRef } from 'react';
import { usePayableAmount } from 'context/hooks/usePayableAmount';
import Button from 'components/ui/Button/Button';
import { usePageContext } from 'context/hooks/usePageContext';
import { useTransactionContext } from 'context/hooks/useTransactionContext';
import { PAGES } from 'context/pageContext';
import { OpayHandler } from 'services/opayHandler/OpayHandler';
import { SpinnerInline } from 'components/ui/Spinner';

type OpayState = 'idle' | 'loading' | 'redirected' | 'error';

const opayHandler = new OpayHandler();

const PayWithOpay = () => {
  const [state, setState] = useState<OpayState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [referenceId, setReferenceId] = useState('');
  const pollRef = useRef<NodeJS.Timeout | null>(null);
  const popupWindowRef = useRef<Window | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { changePage } = usePageContext();
  const {
    ceviantTransactionId,
    currency,
    customerEmail,
    customerPhone,
    customerId,
    merchantName,
  } = useTransactionContext();

  const { totalAmount } = usePayableAmount('OPAY');

  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (popupWindowRef.current && !popupWindowRef.current.closed) {
        popupWindowRef.current.close();
      }
    };
  }, []);

  const closePopupWithDelay = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
    }

    closeTimerRef.current = setTimeout(() => {
      if (popupWindowRef.current && !popupWindowRef.current.closed) {
        popupWindowRef.current.close();
      }
    }, 450);
  };

  useEffect(() => {
    if (state !== 'redirected' || !referenceId) return;

    pollRef.current = setInterval(async () => {
      try {
        const status = await opayHandler.getStatus(referenceId);
        if (status.status === 'APPROVED' || status.status === 'SUCCESS') {
          clearInterval(pollRef.current!);
          closePopupWithDelay();
          changePage(PAGES.SUCCESS);
        } else if (
          status.status === 'FAIL' ||
          status.status === 'CLOSE' ||
          status.status === 'DECLINED'
        ) {
          clearInterval(pollRef.current!);
          setState('error');
          setErrorMessage(
            status.failureReason || status.message || 'Payment failed',
          );
        }
      } catch {
        clearInterval(pollRef.current!);
        setState('error');
        setErrorMessage(
          'Unable to verify payment status. Please contact support.',
        );
      }
    }, 5000);

    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [state, referenceId, changePage]);

  const handlePay = async () => {
    if (state !== 'idle') return;
    setState('loading');
    try {
      const response = await opayHandler.initiatePayment({
        reference: ceviantTransactionId,
        amount: totalAmount / 100,
        currency,
        country: 'NG',
        returnUrl: '',
        cancelUrl: '',
        callbackUrl: '',
        productName: merchantName,
        merchantName,
        productDescription: '',
        customerEmail,
        customerPhone,
        customerUserId: customerId,
        customerUserName: customerEmail,
      });

      if (!response.cashierUrl) {
        setState('error');
        setErrorMessage(
          response.message || 'Payment initiation failed. Please try again.',
        );
        return;
      }

      setReferenceId(response.referenceId);
      popupWindowRef.current = window.open(
        response.cashierUrl,
        'OpayPayment',
        'width=600,height=800,menubar=no,toolbar=no',
      );
      setState('redirected');
    } catch (error: any) {
      setState('error');
      setErrorMessage(
        error?.response?.data?.message ||
          'Payment initiation failed. Please try again.',
      );
    }
  };

  return (
    <section
      style={{ fontFamily: 'Inter, sans-serif' }}
      className="w-full md:max-w-sm mx-auto flex flex-col items-center justify-center md:py-8 gap-6"
    >
      {state === 'idle' && (
        <div className="text-center space-y-8">
          <h1 className="text-center">
            Click the button below to authorize your payment with your Opay
            wallet
          </h1>
          <Button onClick={handlePay}>Pay with Opay</Button>
        </div>
      )}

      {state === 'loading' && <SpinnerInline />}

      {state === 'redirected' && (
        <div className="text-center space-y-4">
          <p className="font-semibold text-lg text-[#535353]">
            Complete your payment in the OPay window
          </p>
          <p className="text-sm text-gray-500">
            This page will update automatically once payment is confirmed.
          </p>
          <SpinnerInline />
        </div>
      )}

      {state === 'error' && (
        <div className="text-center space-y-8">
          <p className="text-red-600 font-semibold">{errorMessage}</p>
          <div className="flex gap-4 w-full">
            <Button
              variant="outline"
              type="button"
              onClick={() =>
                window.parent.postMessage({ message: 'closeCeviantSDK' }, '*')
              }
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </section>
  );
};

export default PayWithOpay;
